import { useEffect, useState, type ReactNode } from 'react';
import type { ClientSnapshot } from '@lw/client-core';
import { useRuntime } from '../runtime/useRuntime.js';
import type { Runtime } from '../runtime/runtime.js';
import { ArmGate, FlashOverlay, IndicatorPill, MilestoneBar, StationsStrip } from '../components/Shell.js';
import { Ticker, TickerMobile } from '../components/Common.js';
import { UavView } from '../views/UavView.js';
import { FdcView } from '../views/FdcView.js';
import { ObserverView } from '../views/ObserverView.js';
import { fmtTime } from './helpers.js';
import { devTag } from '../runtime/identity.js';

export function App(): ReactNode {
  const { rt, state } = useRuntime();
  const s = state.snapshot;
  const role = rt.id.role;
  const roleName = { UAV: 'UAV', FDC: 'FDC', OBSERVER: 'Observer', ADMIN: 'Admin' }[role];
  const active = s && s.state !== 'IDLE' && s.state !== 'SAVED' && s.state !== 'DISCARDED';
  const seatHolder = role === 'UAV' || role === 'FDC' ? s?.seats[role] : null;
  const mine = !seatHolder || seatHolder.deviceId === rt.id.deviceId;
  const queued = rt.client.outbox.pending().length;
  return (
    <>
      <header className="topbar">
        <span className="roletag">{roleName} · {rt.id.user}</span>
        <IndicatorPill />
        <div className="badges">
          {(role === 'UAV' || role === 'FDC') && active ? (
            mine ? <span className="badge ok" data-testid="seat">In control</span> : <button className="badge warn clickable" data-testid="seat" onClick={() => rt.client.send('TAKE_SEAT', {}, null)}>Viewing — take control</button>
          ) : null}
          {queued ? <span className="badge warn" data-testid="queued">Queued ({queued})</span> : null}
          {role !== 'ADMIN' && active ? (rt.audio.armed ? <span className="badge ok">Audio armed</span> : <button className="badge warn clickable" onClick={() => void rt.audio.arm().then(() => rt.log('audio armed', 'good'))}>Tap to arm</button>) : null}
          {!rt.stores.protected() ? <span className="badge bad">No offline protection</span> : null}
          {role !== 'ADMIN' && !rt.cues.leader && active && !document.hidden ? <span className="badge warn">Audio on other tab</span> : null}
        </div>
      </header>
      <StationsStrip />
      {s && active ? <MilestoneBar snapshot={s} /> : null}
      <div className="body">
        <Body snapshot={s} />
      </div>
      <FlashOverlay />
      <ArmGate />
    </>
  );
}

function Body({ snapshot: s }: { snapshot: ClientSnapshot | null }): ReactNode {
  if (!s) return <main className="main no-actions"><div className="main-inner"><div className="card"><div className="eyebrow">Connecting</div><h2 className="big">Waiting for the server</h2><p>The last known state is shown as soon as it is available.</p></div></div></main>;
  return <Screen snapshot={s} />;
}

function Screen({ snapshot: s }: { snapshot: ClientSnapshot }): ReactNode {
  const { rt } = useRuntime();
  const role = rt.id.role;
  const full = fullscreenFor(rt, s);
  if (full) return full;
  // each role screen is its own component so the views' hooks have a stable owner
  return role === 'UAV' ? <UavScreen snapshot={s} /> : role === 'FDC' ? <FdcScreen snapshot={s} /> : <ObserverScreen snapshot={s} />;
}

function Layout({ snapshot: s, v }: { snapshot: ClientSnapshot; v: { main: ReactNode; actions?: ReactNode } }): ReactNode {
  return (
    <>
      <div className="workarea">
        <main className="main">
          <div className="main-inner">
            <Banners snapshot={s} />
            {v.main}
            <TickerMobile />
          </div>
        </main>
        {v.actions}
      </div>
      <Ticker />
    </>
  );
}

function UavScreen({ snapshot }: { snapshot: ClientSnapshot }): ReactNode {
  const v = UavView({ snapshot });
  return <Layout snapshot={snapshot} v={v} />;
}
function FdcScreen({ snapshot }: { snapshot: ClientSnapshot }): ReactNode {
  const v = FdcView({ snapshot });
  return <Layout snapshot={snapshot} v={v} />;
}
function ObserverScreen({ snapshot }: { snapshot: ClientSnapshot }): ReactNode {
  const v = ObserverView({ snapshot });
  return <Layout snapshot={snapshot} v={v} />;
}

/** full-screen states (pure: no hooks, so the caller's hook order is stable) */
function fullscreenFor(rt: Runtime, s: ClientSnapshot): ReactNode {
  const role = rt.id.role;
  const now = rt.client.chrono.serverNow();
  if (role === 'OBSERVER' && (s.state === 'IDLE' || s.state === 'SAVED' || s.state === 'DISCARDED')) {
    return <div className="fullscreen" data-testid="no-mission"><div className="giant muted">No fire mission</div><p>You will see the call for fire here the moment the UAV sends one.</p></div>;
  }
  const tl = s.timeline;
  if (s.state === 'IN_FLIGHT' && tl && now !== null && now >= tl.tImpact) {
    return (
      <div className="fullscreen impact" data-testid="impact">
        <div className="giant">Impact</div>
        <p>{fmtTime(tl.tImpact, true)} · {s.target?.value} · code {s.solution?.laserCode}</p>
        {role === 'UAV' ? (
          <div className="actions">
            {s.uav.laserOffAt ? <div className="chip ok" style={{ justifyContent: 'center', fontSize: 16 }}>Laser off ✓</div> : <button className="btn confirm huge" data-testid="laser-off" onClick={() => rt.client.send('LASER_OFF', {}, { expectedState: 'IN_FLIGHT' })}>Laser off</button>}
          </div>
        ) : null}
      </div>
    );
  }
  if (role === 'UAV' && s.state === 'WEAPON_READY' && s.lastCancel?.band === 'CHECK_FIRE' && !s.uav.ceaseLasingAckAt) {
    return (
      <div className="fullscreen cease" data-testid="cease">
        <div className="giant red">Cease lasing</div>
        <p>The FDC cancelled the fire inside the laser window. Turn the designator off, then acknowledge.</p>
        <div className="actions"><button className="btn confirm huge" data-testid="cease-ack" onClick={() => rt.client.send('CEASE_LASING_ACK', {}, { expectedState: 'WEAPON_READY' })}>Laser is off</button></div>
      </div>
    );
  }
  return null;
}

function Banners({ snapshot: s }: { snapshot: ClientSnapshot }): ReactNode {
  const { rt, state } = useRuntime();
  const role = rt.id.role;
  const ind = rt.client.indicator.current();
  const inCountdown = s.state === 'IN_FLIGHT';
  const out: ReactNode[] = [];
  const seatHolder = role === 'UAV' || role === 'FDC' ? s.seats[role] : null;
  const [takeover, setTakeover] = useState<string | null>(null);
  useEffect(() => {
    const last = [...state.events].reverse().find((e) => e.type === 'SEAT_TAKEN' && e.payload['claim'] === false && e.payload['role'] === role);
    if (last && (last.payload['toDevice'] as string) !== rt.id.deviceId && seatHolder && seatHolder.deviceId !== rt.id.deviceId) setTakeover(`${String(last.payload['user'])}@${devTag(last.payload['toDevice'] as string)} at ${fmtTime(last.tEvent)}`);
    else setTakeover(null);
  }, [state.events, seatHolder, role, rt.id.deviceId]);
  if (takeover) out.push(<div key="takeover" className="banner amber"><div className="grow"><b>Control taken</b> by {takeover} — you are viewing.</div><button className="btn" onClick={() => rt.client.send('TAKE_SEAT', {}, null)}>Take control</button></div>);
  const pf = state.pendingFire;
  if (role === 'FDC' && pf && !pf.acknowledgedBanner && pf.linkFlags.includes('FIRE_WITH_UAV_LINK_LOST')) {
    out.push(<div key="peerlost" className="banner red" data-testid="banner-uav-lost"><div className="grow"><b>UAV link lost — fire recorded and sent.</b> Cues may not reach the designator. Confirm by voice.</div><button className="btn" onClick={() => rt.acknowledgeFireBanner()}>Acknowledge</button><button className="btn ghost" onClick={() => rt.client.send('CANCEL_FIRE', {}, { expectedState: 'IN_FLIGHT' })}>Cancel fire</button></div>);
  }
  if (role === 'FDC' && pf && pf.linkFlags.includes('FIRE_WITH_OWN_LINK_LOST')) {
    out.push(<div key="ownlost" className="banner red" data-testid="banner-own-lost"><div className="grow"><b>Your link is down — fire recorded, will send.</b> Press time is preserved; every viewer will align the moment the link returns.</div><button className="btn ghost" onClick={() => rt.client.send('CANCEL_FIRE', {}, { expectedState: 'IN_FLIGHT' })}>Cancel fire</button></div>);
  }
  if (inCountdown && ind.colour === 'RED') out.push(<div key="red" className="banner red" data-testid="banner-red"><div className="grow"><b>Connection lost</b> — countdown running on last sync ({rt.client.indicator.label().value || '±?'}, growing). {role === 'FDC' ? 'CANCEL / CHECK FIRE will be queued and judged on arrival.' : 'A cancel from the FDC may not reach you.'} Confirm by voice.</div></div>);
  else if (inCountdown && rt.client.chrono.currentMode() === 'DEGRADED_WALL') out.push(<div key="deg" className="banner amber slim"><div className="grow"><b>Sync degraded</b> — device slept; running on the wall clock until the next sample.</div></div>);
  else if (inCountdown && ind.colour === 'ORANGE') out.push(<div key="orange" className="banner amber slim"><div className="grow"><b>Connection intermittent</b> — {rt.client.indicator.label().label.toLowerCase()} {rt.client.indicator.label().value}.</div></div>);
  const peer = role === 'UAV' ? 'FDC' : role === 'FDC' ? 'UAV' : null;
  if (peer && (inCountdown || s.state === 'UAV_READY') && (s.links[peer]?.conn === 'LOST' || (rt.client.pair !== null && !rt.client.pair.known)) && ind.colour !== 'RED' && !(pf && pf.linkFlags.includes('FIRE_WITH_UAV_LINK_LOST'))) {
    out.push(<div key="peer" className="banner red slim" data-testid="banner-peer-lost"><div className="grow"><b>{peer} link lost</b> — {role === 'FDC' ? 'cues may not reach the designator.' : 'the FDC cannot see your state.'} Confirm by voice.</div></div>);
  }
  if (s.minClientBuild && s.minClientBuild !== '0.0.0' && rt.clientBuild !== 'dev' && s.minClientBuild > rt.clientBuild) out.push(<div key="upd" className="banner amber slim"><div className="grow"><b>Update required when the mission ends.</b></div></div>);
  return <>{out}</>;
}
